import { openDB } from 'idb';

const DB_NAME = 'fmiq-offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  QUEUE: 'offline-queue',
  WORK_ORDERS: 'work-orders',
  PHOTOS: 'offline-photos',
  SYNC_STATUS: 'sync-status'
};

// Initialize the database
const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Queue for offline API calls
      if (!db.objectStoreNames.contains(STORES.QUEUE)) {
        const queueStore = db.createObjectStore(STORES.QUEUE, { keyPath: 'id', autoIncrement: true });
        queueStore.createIndex('timestamp', 'timestamp');
        queueStore.createIndex('type', 'type');
      }
      
      // Cached work orders for offline viewing
      if (!db.objectStoreNames.contains(STORES.WORK_ORDERS)) {
        const woStore = db.createObjectStore(STORES.WORK_ORDERS, { keyPath: 'id' });
        woStore.createIndex('status', 'status');
        woStore.createIndex('assigned_to', 'assigned_to');
      }
      
      // Photos taken offline (to upload later)
      if (!db.objectStoreNames.contains(STORES.PHOTOS)) {
        const photoStore = db.createObjectStore(STORES.PHOTOS, { keyPath: 'id', autoIncrement: true });
        photoStore.createIndex('work_order_id', 'work_order_id');
        photoStore.createIndex('synced', 'synced');
      }
      
      // Sync status tracking
      if (!db.objectStoreNames.contains(STORES.SYNC_STATUS)) {
        db.createObjectStore(STORES.SYNC_STATUS, { keyPath: 'key' });
      }
    }
  });
};

// Get database instance
let dbInstance = null;
const getDB = async () => {
  if (!dbInstance) {
    dbInstance = await initDB();
  }
  return dbInstance;
};

// ==================== OFFLINE QUEUE ====================

export const addToQueue = async (request) => {
  const db = await getDB();
  const item = {
    ...request,
    timestamp: Date.now(),
    synced: false,
    retries: 0
  };
  await db.add(STORES.QUEUE, item);
  return item;
};

export const getQueueItems = async () => {
  const db = await getDB();
  return db.getAllFromIndex(STORES.QUEUE, 'timestamp');
};

export const getQueueCount = async () => {
  const db = await getDB();
  const items = await db.getAll(STORES.QUEUE);
  return items.filter(i => !i.synced).length;
};

export const markQueueItemSynced = async (id) => {
  const db = await getDB();
  const item = await db.get(STORES.QUEUE, id);
  if (item) {
    item.synced = true;
    item.syncedAt = Date.now();
    await db.put(STORES.QUEUE, item);
  }
};

export const removeQueueItem = async (id) => {
  const db = await getDB();
  await db.delete(STORES.QUEUE, id);
};

export const clearSyncedItems = async () => {
  const db = await getDB();
  const items = await db.getAll(STORES.QUEUE);
  for (const item of items) {
    if (item.synced) {
      await db.delete(STORES.QUEUE, item.id);
    }
  }
};

// ==================== WORK ORDER CACHE ====================

export const cacheWorkOrders = async (workOrders) => {
  const db = await getDB();
  const tx = db.transaction(STORES.WORK_ORDERS, 'readwrite');
  for (const wo of workOrders) {
    await tx.store.put(wo);
  }
  await tx.done;
  
  // Update sync status
  await db.put(STORES.SYNC_STATUS, {
    key: 'work_orders_last_sync',
    timestamp: Date.now()
  });
};

export const getCachedWorkOrders = async () => {
  const db = await getDB();
  return db.getAll(STORES.WORK_ORDERS);
};

export const getCachedWorkOrder = async (id) => {
  const db = await getDB();
  return db.get(STORES.WORK_ORDERS, id);
};

export const updateCachedWorkOrder = async (id, updates) => {
  const db = await getDB();
  const wo = await db.get(STORES.WORK_ORDERS, id);
  if (wo) {
    await db.put(STORES.WORK_ORDERS, { ...wo, ...updates, _localUpdate: true });
  }
};

// ==================== PHOTO STORAGE ====================

export const saveOfflinePhoto = async (workOrderId, photoData, metadata = {}) => {
  const db = await getDB();
  const photo = {
    work_order_id: workOrderId,
    data: photoData, // Base64 or Blob
    metadata,
    timestamp: Date.now(),
    synced: false
  };
  const id = await db.add(STORES.PHOTOS, photo);
  return { ...photo, id };
};

export const getOfflinePhotos = async (workOrderId = null) => {
  const db = await getDB();
  if (workOrderId) {
    return db.getAllFromIndex(STORES.PHOTOS, 'work_order_id', workOrderId);
  }
  return db.getAll(STORES.PHOTOS);
};

export const getUnsyncedPhotos = async () => {
  const db = await getDB();
  const photos = await db.getAllFromIndex(STORES.PHOTOS, 'synced', false);
  return photos;
};

export const markPhotoSynced = async (id, uploadedUrl) => {
  const db = await getDB();
  const photo = await db.get(STORES.PHOTOS, id);
  if (photo) {
    photo.synced = true;
    photo.syncedAt = Date.now();
    photo.uploadedUrl = uploadedUrl;
    await db.put(STORES.PHOTOS, photo);
  }
};

export const deleteOfflinePhoto = async (id) => {
  const db = await getDB();
  await db.delete(STORES.PHOTOS, id);
};

// ==================== SYNC STATUS ====================

export const getSyncStatus = async (key) => {
  const db = await getDB();
  const status = await db.get(STORES.SYNC_STATUS, key);
  return status?.timestamp || null;
};

export const setSyncStatus = async (key, timestamp = Date.now()) => {
  const db = await getDB();
  await db.put(STORES.SYNC_STATUS, { key, timestamp });
};

// ==================== CLEAR ALL ====================

export const clearAllOfflineData = async () => {
  const db = await getDB();
  await db.clear(STORES.QUEUE);
  await db.clear(STORES.WORK_ORDERS);
  await db.clear(STORES.PHOTOS);
  await db.clear(STORES.SYNC_STATUS);
};

export default {
  addToQueue,
  getQueueItems,
  getQueueCount,
  markQueueItemSynced,
  removeQueueItem,
  clearSyncedItems,
  cacheWorkOrders,
  getCachedWorkOrders,
  getCachedWorkOrder,
  updateCachedWorkOrder,
  saveOfflinePhoto,
  getOfflinePhotos,
  getUnsyncedPhotos,
  markPhotoSynced,
  deleteOfflinePhoto,
  getSyncStatus,
  setSyncStatus,
  clearAllOfflineData
};
